import { useState } from 'react'
import clsx from 'clsx'
import { DataTable, type DataTableColumn } from '@/components/DataTable'
import type { Firm, FirmUsagePlan, ProductTypesFile } from '@/types/bundle'
import { entityLabel } from '../overrideUtils'

export interface UsagePlanEditorProps {
  firm: Firm
  productTypes: ProductTypesFile
  onChange: (usagePlan: FirmUsagePlan | undefined) => void
}

// usage_plan.model/period are schema `const`s ("requests"/"monthly" are the
// only values the real schema accepts today) - shown as a fixed caption,
// not a control, since there's nothing to choose.
const PLAN_CAPTION = 'Requests per month, per product. Leave blank for unlimited.'

interface UsageRow {
  productId: string
}

// Per-client monthly request quotas - previously invisible anywhere in this
// app (usage_plan was deliberately untyped/unrendered, per a since-outdated
// Frontend PRD 06 scope note). This is the only screen that edits Firm data
// at all, so it's the natural home for it. Rendered as a DataTable, the
// same bordered/sticky-header component the Overrides section right below
// it already uses - loose label/input rows with no visual grouping read as
// disconnected floating elements next to that table.
export function UsagePlanEditor({ firm, productTypes, onChange }: UsagePlanEditorProps) {
  const [touched, setTouched] = useState<Set<string>>(new Set())

  // Real product catalog first, then any extra ids already set on this
  // firm's usage_plan that aren't (or are no longer) a real product type.
  // Confirmed against the real running draft: a firm can have a quota entry
  // for a product it doesn't currently have enabled (usage_plan isn't
  // scoped to enabled_products at the schema level) - showing catalog ids
  // only would silently drop that entry on the next save.
  const catalogIds = Object.keys(productTypes.product_types)
  const extraIds = Object.keys(firm.usage_plan?.products ?? {}).filter((id) => !catalogIds.includes(id))
  const rows: UsageRow[] = [...catalogIds, ...extraIds].map((productId) => ({ productId }))

  function setQuota(productId: string, maxRequests: number | undefined) {
    const nextProducts = { ...(firm.usage_plan?.products ?? {}) }
    if (maxRequests === undefined) {
      delete nextProducts[productId]
    } else {
      nextProducts[productId] = { max_requests: maxRequests }
    }

    // No quotas left at all -> drop the whole block rather than keep a
    // pointless empty object around; absent usage_plan already means
    // unlimited, matching CONFIG_REFERENCE.md's documented semantics.
    onChange(Object.keys(nextProducts).length === 0 ? undefined : { model: 'requests', period: 'monthly', products: nextProducts })
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-col gap-1">
        <span className="text-meta font-medium text-text-secondary">Usage plan</span>
        <span className="text-meta text-text-disabled">No product types defined yet</span>
      </div>
    )
  }

  const columns: DataTableColumn<UsageRow>[] = [
    {
      key: 'product',
      header: 'Product',
      render: (row) => entityLabel('product_types', row.productId, productTypes),
    },
    {
      key: 'quota',
      header: 'Monthly quota',
      align: 'right',
      render: (row) => {
        const current = firm.usage_plan?.products?.[row.productId]?.max_requests
        const isInvalid =
          touched.has(row.productId) && current !== undefined && (!Number.isInteger(current) || current < 1)

        return (
          <div className="flex flex-col items-end gap-1">
            <input
              type="number"
              min={1}
              step={1}
              value={current ?? ''}
              placeholder="Unlimited"
              onChange={(event) => {
                const raw = event.target.value
                setQuota(row.productId, raw === '' ? undefined : Number(raw))
              }}
              onBlur={() => setTouched((prev) => new Set(prev).add(row.productId))}
              className={clsx(
                'w-32 rounded-control border bg-bg px-3 py-2 text-right text-body text-text-primary outline-none placeholder:text-text-disabled',
                isInvalid ? 'border-danger' : 'border-border',
              )}
            />
            {isInvalid && <span className="text-meta text-danger">Must be a whole number, at least 1</span>}
          </div>
        )
      },
    },
  ]

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        <span className="text-meta font-medium text-text-secondary">Usage plan</span>
        <span className="text-meta text-text-disabled">{PLAN_CAPTION}</span>
      </div>
      <DataTable columns={columns} data={rows} getRowKey={(row) => row.productId} />
    </div>
  )
}
